import { useState, useEffect, useCallback, useReducer } from "react";
import { api } from "../lib/api";
import { idsFor } from "../lib/modules";
import { publicIpRequest } from "../../utils/publicIpTargets";
// A leaf util with no UI of its own, same allowance as report/shapeReport in
// scanner/ScanResults: reimplementing the report renderer would fork it.
import generateSecurityPDF from "../../components/pdfGenerator";

// Prospecting mode. A different endpoint from the normal scan, not a flag on it:
// nothing is filed against a customer organization, which is the whole point.
//
// The state lives in a module-level store rather than in component state, and that is
// load-bearing rather than tidy. A prospecting scan takes minutes, and every screen in
// v2 unmounts the moment you navigate — switching prospecting tabs, or leaving for
// Report Templates and coming back. With the state inside the component, doing either
// destroyed `running` and `results`: the request carried on and the server still filed
// the scan, but the interface showed an empty form with no sign a scan had ever run and
// no route to the result. A store outside React survives every unmount short of a page
// reload, which is the actual lifetime of the thing being modelled.
const scanStore = {
  running: false,
  results: null,
  error: "",
  // Which domain the on-screen results belong to. Kept beside them so the two can never
  // disagree, and so it survives the same unmounts they do.
  scanned: null,
  listeners: new Set(),
  set(patch) {
    Object.assign(this, patch);
    this.listeners.forEach((notify) => notify());
  },
};

export function useProspectScan() {
  const [, force] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    scanStore.listeners.add(force);
    return () => { scanStore.listeners.delete(force); };
  }, []);

  const run = useCallback(async ({ domain, groups, reportStyle, company, publicIps, publicIpMode = "auto" }) => {
    scanStore.set({ running: true, error: "", results: null, scanned: null });
    const prospectCompany = typeof company === "string" ? company.trim().slice(0, 255) : "";
    try {
      const data = await api.post("s2/api/prospect/scan", {
        target: domain,
        targetType: "domain",
        modules: idsFor(groups),
        ...publicIpRequest({ target: domain, enabled: groups.includes("infra"), mode: publicIpMode, manual: publicIps || "" }),
        hibpEmails: [],           // prospecting never collects breach emails
        report_style: reportStyle,
        // Used for organization IP attribution and saved with the report. This is
        // the prospect's company, independently of any selected customer record.
        prospect_company: prospectCompany,
        prospecting: true,
        oneTime: true,
      });
      scanStore.set({
        results: data,
        // prospectCompany, not company: ReportActions renders both a live scan and a
        // history row through the same props, and the API spells it this way.
        // No reportTheme: it is chosen on the report bar, at the moment an output is
        // generated, so there is nothing to carry from the scan.
        scanned: {
          domain, reportStyle, prospectCompany,
          scanId: data?.scanId || data?.scan_id || null,
        },
      });
      return data;
    } catch (e) {
      scanStore.set({ error: e.message || "Scan failed." });
      return null;
    } finally {
      scanStore.set({ running: false });
    }
  }, []);

  // Clearing is explicit — the results stay until the user starts another scan or
  // dismisses them, not until they happen to navigate.
  const clear = useCallback(() => {
    scanStore.set({ results: null, scanned: null, error: "" });
  }, []);

  return {
    run, clear,
    running: scanStore.running,
    results: scanStore.results,
    error: scanStore.error,
    scanned: scanStore.scanned,
  };
}

export function useProspectHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("s2/api/prospect/history?limit=50&offset=0");
      // `scans`, not `history` or `result`. The route returns { scans, total, limit,
      // offset } — reading the wrong key meant this list was ALWAYS empty and the tab
      // permanently showed its "no scans yet" state, however many scans had been run.
      setItems(data?.scans || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load history.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (scanId) => {
    await api.del(`s2/api/prospect/delete/${scanId}`);
    setItems((prev) => prev.filter((x) => x.scanId !== scanId));
  }, []);

  return { items, loading, error, reload: load, remove };
}

// Email a scan's report to the prospect.
//
// The PDF is rendered in the browser and posted as base64, which is how v1 does it —
// the server has no renderer, it only attaches what it is given. That is also why this
// is slow enough to need its own busy state: generating the PDF is the expensive half.
export function useSendProspectReport() {
  const [sending, setSending] = useState(false);

  const send = useCallback(async ({ scanId, results, domain, to, name, company, subject, body, theme }) => {
    // The route is keyed on the scan, so without one there is nothing to send against —
    // worth saying plainly rather than posting to `/resend/undefined` and reading a 404.
    if (!scanId) throw new Error("This report has no saved scan to send against.");
    setSending(true);
    try {
      const blob = await generateSecurityPDF(results, domain, {
        theme, prospectName: name || "", prospectCompany: company || "",
        isCyberMode: false, saveFile: false,
      });
      const pdfBase64 = await blobToBase64(blob);

      // /api/prospect/resend/:scanId, not the /api/prospect-report/send this used to
      // call — that path exists in v1's client too but has never existed on the server,
      // so it has always been a 404. The route below is the one that actually mails the
      // PDF, flips the scan to "sent" and writes the row in prospect_email_history.
      //
      // It is keyed on the scan because everything else about the report is already
      // stored against it; only the recipient, the wording and the rendered PDF come
      // from this dialog.
      return await api.post(`s2/api/prospect/resend/${scanId}`, {
        email: to, subject, message: body,
        prospectName: name || "", prospectCompany: company || "",
        pdfBase64, pdfName: `security-report-${domain}-${Date.now()}.pdf`,
      });
    } finally { setSending(false); }
  }, []);

  return { send, sending };
}

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  // Strip the "data:application/pdf;base64," prefix — the API wants the payload alone.
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

// Email templates, shared by prospecting and scan links. The two differ only by
// template_type, which is why this takes it as an argument rather than existing twice.
export function useEmailTemplates(templateType) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const path = templateType === "prospect" ? "s2/api/prospect/templates" : "s2/api/email-templates";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(path);
      // Both endpoints return every type; the caller's type is filtered client-side,
      // matching the original screens.
      setTemplates((data?.templates || []).filter((t) => t.template_type === templateType));
      setError("");
    } catch (e) {
      setError(e.message || "Could not load templates.");
    } finally { setLoading(false); }
  }, [path, templateType]);

  useEffect(() => { load(); }, [load]);

  // Returns the saved row so a caller can keep it selected afterwards. Without this the
  // only way back to the record you just wrote is to search the reloaded list by name,
  // which breaks the moment a template is renamed.
  const save = useCallback(async ({ id, name, subject, body }) => {
    const payload = { name, subject, body, template_type: templateType };
    const data = id ? await api.put(`${path}/${id}`, payload) : await api.post(path, payload);
    await load();
    return data?.template || null;
  }, [path, templateType, load]);

  const remove = useCallback(async (id) => {
    await api.del(`${path}/${id}`);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, [path]);

  return { templates, loading, error, reload: load, save, remove };
}
